/**
 * `useMe()` — fetches /api/v1/me using a fresh MSAL access token.
 *
 * Returns `{ data, loading, error }` and re-runs when the active account
 * changes. The token is acquired through MSAL silently (with popup
 * fallback). The /me response is cached in component state only; React
 * Query / SWR could plug in later, but the simple version is enough for
 * Phase 1.
 */

import { useCallback, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { ApiError } from "@/shared/api/apiFetch";
import { type Me, getMe } from "@/shared/api/me";

interface MeState {
  data: Me | null;
  loading: boolean;
  error: string | null;
  /** The API error `code` (e.g. `account_not_provisioned`) when the failure
   *  came from the backend envelope, so callers can branch on it. */
  errorCode: string | null;
}

export function useMe(): MeState & {
  reload: () => void;
  /** Replace the cached profile, e.g. after PATCH /me, without a refetch. */
  update: (next: Me) => void;
} {
  const { accounts } = useMsal();
  const getToken = useAccessToken();
  const [state, setState] = useState<MeState>({
    data: null,
    loading: true,
    error: null,
    errorCode: null,
  });
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const update = useCallback((next: Me) => setState((s) => ({ ...s, data: next })), []);

  useEffect(() => {
    if (accounts.length === 0) {
      setState({ data: null, loading: false, error: null, errorCode: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null, errorCode: null }));
    (async () => {
      try {
        const token = await getToken();
        const me = await getMe(token);
        if (!cancelled) setState({ data: me, loading: false, error: null, errorCode: null });
      } catch (err) {
        if (!cancelled) {
          setState({
            data: null,
            loading: false,
            error: err instanceof Error ? err.message : String(err),
            errorCode: err instanceof ApiError ? err.code : null,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accounts, getToken, reloadKey]);

  return { ...state, reload, update };
}
